// Copyright 2022 - 2023 Fewcha. All rights reserved.

import { createAction, createAsyncThunk, createReducer } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import axios from "axios";
import dayjs from "dayjs";
import { defaultConfigReducer } from "features/config/types";
import { defaulEventReducer, EvenResponse, EventReducer } from "./types";

// `YYYY-MM-DDTHH:mm:ss.sssZ`
export const getEvent = createAsyncThunk("event/getEvent", async (): Promise<EventReducer> => {
  const res = await axios.get<EvenResponse>(defaultConfigReducer.base.EVENT_URL, {
    headers: { "Cache-Control": "no-cache" },
  });

  const data = res.data;
  if (!data) {
    return defaulEventReducer;
  }

  return parseEvent(data);
});

export const parseStatus = createAsyncThunk(
  "event/parseStatus",
  async (_, { getState }): Promise<{ isStarted: boolean; isEnded: boolean; checkWhitelist: boolean }> => {
    const { event } = getState() as { event: EventReducer };

    const now = dayjs();

    const { startTime, endTime, paused } = event;
    const start = dayjs(startTime);
    const end = dayjs(endTime);

    let isStarted = false;
    let isEnded = false;
    let checkWhitelist = false;

    if (now.isAfter(start)) {
      isStarted = true;
      if (paused) {
        isEnded = true;
      }
    }

    if (now.isBefore(end)) {
      checkWhitelist = true;
    }

    return {
      isStarted,
      isEnded,
      checkWhitelist,
    };
  },
);

export const parseEventState = createAsyncThunk(
  "event/parseEventState",
  async (_, { getState }): Promise<EventReducer> => {
    const { event } = getState() as { event: EventReducer };
    return parseEvent(event);
  },
);

export const parseEvent = (data: { startTime: string; endTime: string; paused: boolean }) => {
  const now = dayjs();

  const { startTime, endTime, paused } = data;
  const start = dayjs(startTime);
  const end = dayjs(endTime);

  let isStarted = false;
  let isEnded = false;
  let checkWhitelist = false;

  if (now.isAfter(start)) {
    if (paused) {
      isEnded = true;
    } else {
      isStarted = true;
      if (now.isBefore(end)) {
        checkWhitelist = true;
      }
    }
  }

  return {
    startTime,
    endTime,
    paused,

    isStarted,
    isEnded,
    checkWhitelist,
  };
};

export const updateEvent = createAction<{ key: string; value: any }>("event/updateEvent");

const eventReducer = createReducer(defaulEventReducer, (builder) => {
  builder
    .addCase(getEvent.fulfilled, (_, action) => {
      return action.payload;
    })

    .addCase(parseEventState.fulfilled, (_, action) => {
      return action.payload;
    })
    .addCase(updateEvent, (state, action) => {
      const d: EventReducer = { ...state };
      (d as any)[action.payload.key as any] = action.payload.value;
      return parseEvent(d);
    })
    .addCase(parseStatus.fulfilled, (state, action) => {
      const { isStarted, isEnded, checkWhitelist } = action.payload;
      state.isStarted = isStarted;
      state.isEnded = isEnded;
      state.checkWhitelist = checkWhitelist;
    });
});

export const selectEvent = (state: RootState) => state.event;

export default eventReducer;
